import pb from '../lib/pocketbase';
import { debugLog } from './debugConfig';
import { getCacheableUrl } from '../utils/imageCache';
import type { 
  Partner, 
  Advertisement, 
  PartnerCampaign, 
  MarketingCampaign, 
  PromoCodeRequest,
  Category,
  Filter,
  AdvertisementCategory, 
  ContactInquiry,
  Subscriber,
  Entity
} from '../types';
import { notificationTriggerService } from './notificationTriggerService';

const DEFAULT_PROMOCODE_DELIVERY_METHODS = {
  web_display: true,
  email: false
};

const parseDeliveryMethods = (raw: any) => {
  try {
    if (!raw) {
      return { ...DEFAULT_PROMOCODE_DELIVERY_METHODS };
    }
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return {
      web_display: typeof parsed?.web_display === 'boolean' ? parsed.web_display : true,
      email: typeof parsed?.email === 'boolean' ? parsed.email : false
    };
  } catch (error) {
    console.warn('Failed to parse promocode_delivery_methods:', error, raw);
    return { ...DEFAULT_PROMOCODE_DELIVERY_METHODS };
  }
};

const serializeDeliveryMethods = (value?: { web_display: boolean; email: boolean }) =>
  JSON.stringify({
    web_display: value?.web_display ?? DEFAULT_PROMOCODE_DELIVERY_METHODS.web_display,
    email: value?.email ?? DEFAULT_PROMOCODE_DELIVERY_METHODS.email
  });

export interface PromoRequestStatSummary {
  advertisement_id: string;
  request_count: number;
}

export interface PromoRequestStatsResponse {
  success: boolean;
  window_days: number;
  window_start: string;
  total_requests: number;
  stats: PromoRequestStatSummary[];
  error?: string;
}

export class PocketBaseDataService {
  // Partners
  static async getPartners(): Promise<{ data: Partner[] | null; error: string | null }> {
    try {
      const records = await pb.collection('partners').getFullList({
        sort: '-created',
      });
      
      // Map PocketBase records to Partner interface
      const partners: Partner[] = records.map(record => ({
        id: record.id,
        name: record.name,
        category: record.category,
        category_id: record.category_id,
        max_campaigns: record.max_campaigns,
        status: record.status || 'active', // Default to 'active' if not set
        campaigns_count: record.campaigns_count || 0,
        logo: record.logo,
        logo_url: record.logo_url,
        web_domain: record.web_domain,
        privacy_policy_url: record.privacy_policy_url,
        // Handle region - could be string (JSON) or already parsed array
        region: typeof record.region === 'string' 
          ? (record.region ? JSON.parse(record.region) : [])
          : (record.region || []),
        // Handle integration_settings - could be string (JSON) or already parsed object
        integration_settings: typeof record.integration_settings === 'string'
          ? (record.integration_settings ? JSON.parse(record.integration_settings) : {})
          : (record.integration_settings || {}),
        // Handle priority_quotas - could be string (JSON) or already parsed object
        priority_quotas: typeof record.priority_quotas === 'string'
          ? (record.priority_quotas ? JSON.parse(record.priority_quotas) : {})
          : (record.priority_quotas || {}),
        created_at: record.created // Map PocketBase 'created' to 'created_at'
      }));
      
      return { data: partners, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async getPromoRequestStats(days = 7): Promise<{ data: PromoRequestStatsResponse | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Fetching promo request stats...', { days });
      const params = new URLSearchParams({ days: String(days) });
      const response = await pb.send(`/api/promocodes/stats?${params.toString()}`, {
        method: 'GET'
      });

      await debugLog('PocketBase: Promo request stats response:', response);

      if (response?.success) {
        return { data: response as PromoRequestStatsResponse, error: null };
      }

      return { data: null, error: response?.error || 'Failed to fetch promo request stats' };
    } catch (error: any) {
      console.error('PocketBase: Error fetching promo request stats:', error);
      const errorMessage = error?.data?.error || error.message || 'Failed to fetch promo request stats';
      return { data: null, error: errorMessage };
    }
  }

  static async createPartner(partner: Omit<Partner, 'id' | 'created_at' | 'campaigns_count'>, logoFile?: File | null): Promise<{ data: Partner | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Creating partner...', partner);
      
      // Use FormData if there's a file to upload
      const formData = new FormData();
      
      // Add all partner fields
      formData.append('name', partner.name);
      formData.append('category', partner.category || '');
      formData.append('category_id', partner.category_id || '');
      formData.append('max_campaigns', (partner.max_campaigns || 10).toString());
      formData.append('status', partner.status || 'active');
      formData.append('logo_url', partner.logo_url || '');
      formData.append('web_domain', partner.web_domain || '');
      formData.append('privacy_policy_url', partner.privacy_policy_url || '');
      formData.append('region', JSON.stringify(partner.region || []));
      formData.append('integration_settings', JSON.stringify(partner.integration_settings));
      formData.append('priority_quotas', JSON.stringify(partner.priority_quotas));
      formData.append('campaigns_count', '0');
      
      // Add logo file if provided
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      
      const record = await pb.collection('partners').create(formData);
      
      await debugLog('PocketBase: Partner created successfully');
      
      // Map PocketBase record to Partner interface
      const mappedPartner: Partner = {
        id: record.id,
        name: record.name,
        category: record.category,
        category_id: record.category_id,
        max_campaigns: record.max_campaigns,
        status: record.status || 'active',
        campaigns_count: record.campaigns_count || 0,
        logo: record.logo,
        logo_url: record.logo_url,
        web_domain: record.web_domain,
        privacy_policy_url: record.privacy_policy_url,
        region: typeof record.region === 'string' 
          ? (record.region ? JSON.parse(record.region) : [])
          : (record.region || []),
        integration_settings: typeof record.integration_settings === 'string'
          ? (record.integration_settings ? JSON.parse(record.integration_settings) : {})
          : (record.integration_settings || {}),
        priority_quotas: typeof record.priority_quotas === 'string'
          ? (record.priority_quotas ? JSON.parse(record.priority_quotas) : {})
          : (record.priority_quotas || {}),
        created_at: record.created
      };
      
      return { data: mappedPartner, error: null };
    } catch (error: any) {
      console.error('PocketBase: Create partner error:', error);
      return { data: null, error: error.message };
    }
  }

  static async updatePartner(id: string, updates: Partial<Partner>, logoFile?: File | null): Promise<{ data: Partner | null; error: string | null }> {
    try {
      // Use FormData if there's a file to upload
      if (logoFile) {
        const formData = new FormData();
        
        // Add all update fields
        Object.keys(updates).forEach(key => {
          const value = updates[key as keyof Partner];
          if (value !== undefined && value !== null) {
            if (typeof value === 'object' && !Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else if (Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value.toString());
            }
          }
        });
        
        // Add logo file
        formData.append('logo', logoFile);
        
        const record = await pb.collection('partners').update(id, formData);
        
        // Map PocketBase record to Partner interface
        const mappedPartner: Partner = {
          id: record.id,
          name: record.name,
          category: record.category,
          category_id: record.category_id,
          max_campaigns: record.max_campaigns,
          status: record.status,
          campaigns_count: record.campaigns_count || 0,
          logo: record.logo,
          logo_url: record.logo_url,
          web_domain: record.web_domain,
          privacy_policy_url: record.privacy_policy_url,
          region: typeof record.region === 'string' 
            ? (record.region ? JSON.parse(record.region) : [])
            : (record.region || []),
          integration_settings: typeof record.integration_settings === 'string'
            ? (record.integration_settings ? JSON.parse(record.integration_settings) : {})
            : (record.integration_settings || {}),
          priority_quotas: typeof record.priority_quotas === 'string'
            ? (record.priority_quotas ? JSON.parse(record.priority_quotas) : {})
            : (record.priority_quotas || {}),
          created_at: record.created
        };
        
        return { data: mappedPartner, error: null };
      } else {
        // No file, use regular update
        const record = await pb.collection('partners').update(id, updates);
        
        // Map PocketBase record to Partner interface
        const mappedPartner: Partner = {
          id: record.id,
          name: record.name,
          category: record.category,
          category_id: record.category_id,
          max_campaigns: record.max_campaigns,
          status: record.status,
          campaigns_count: record.campaigns_count || 0,
          logo: record.logo,
          logo_url: record.logo_url,
          web_domain: record.web_domain,
          privacy_policy_url: record.privacy_policy_url,
          region: typeof record.region === 'string' 
            ? (record.region ? JSON.parse(record.region) : [])
            : (record.region || []),
          integration_settings: typeof record.integration_settings === 'string'
            ? (record.integration_settings ? JSON.parse(record.integration_settings) : {})
            : (record.integration_settings || {}),
          priority_quotas: typeof record.priority_quotas === 'string'
            ? (record.priority_quotas ? JSON.parse(record.priority_quotas) : {})
            : (record.priority_quotas || {}),
          created_at: record.created
        };
        
        return { data: mappedPartner, error: null };
      }
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async deletePartner(id: string): Promise<{ error: string | null }> {
    try {
      await pb.collection('partners').delete(id);
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }

  // Advertisements
  static async getAdvertisements(): Promise<{ data: Advertisement[] | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Fetching advertisements...');
      const records = await pb.collection('advertisements').getFullList({
        sort: '-created',
      });
      
      await debugLog('PocketBase: Raw advertisement record count:', records.length);
      
      const mappedRecords: Advertisement[] = await Promise.all(records.map(async record => {
        await debugLog(`🔍 Ad "${record.title}" promocode fields:`, {
          has_promocode_raw: record.has_promocode,
          promocode_type_raw: record.promocode_type,
          has_promocode_mapped: record.has_promocode || false,
          promocode_type_mapped: record.promocode_type || undefined
        });
        
        const deliveryMethods = parseDeliveryMethods(record.promocode_delivery_methods);

        // Construct banner URL - prefer uploaded file, fall back to image_url
        // Use getCacheableUrl to add version parameter for browser caching
        let bannerUrl = record.image_url || '';
        if (record.banner) {
          const rawUrl = pb.files.getURL(record, record.banner);
          bannerUrl = getCacheableUrl(rawUrl, record.updated);
        }

        // Construct provider logo URL if uploaded file exists
        let providerLogoUrl = record.provider_logo_url || '';
        if (record.provider_logo) {
          const rawProviderLogoUrl = pb.files.getURL(record, record.provider_logo);
          providerLogoUrl = getCacheableUrl(rawProviderLogoUrl, record.updated);
        }

        return {
          id: record.id,
          name: record.title,
          description: record.description,
          start_date: record.start_date || '', // Read from PocketBase
          end_date: record.end_date || '', // Read from PocketBase
          banner_url: bannerUrl,
          partner_id: record.partner_id,
          status: record.status,
          approval_status: record.approval_status || undefined, // Keep as undefined if not set
          priority: record.priority || 'bronze', // Default to bronze priority
          advertisement_mode: record.advertisement_mode || 'default',
          categories: record.category ? (typeof record.category === 'string' && record.category.startsWith('[') ? JSON.parse(record.category) : [record.category]) : [],
          region: record.region,
          created_by: record.created_by || '', // Read from PocketBase
          created_at: record.created,
          approved_by: record.approved_by || undefined,
          approved_at: record.approved_at || undefined,
          rejection_reason: record.rejection_reason || undefined,
          // promocode_config removed - use getPromocodeConfig() for admin access
          has_promocode: record.has_promocode || false,
          promocode_type: record.promocode_type || undefined,
          button_text: record.button_text || '',
          badge_text: record.badge_text_ || '', // Note: PocketBase field has underscore
          badge_color: record.badge_color || 'red',
          promocode_delivery_methods: deliveryMethods,
          target_url: record.target_url || '',
          target_url_enabled: record.target_url_enabled || false,
          // Provider information
          provider_entity_id: record.provider_entity_id || undefined,
          provider_name: record.provider_name || undefined,
          provider_logo: record.provider_logo || undefined,
          provider_logo_url: providerLogoUrl || undefined,
          provider_privacy_policy_url: record.provider_privacy_policy_url || undefined
        };
      }));
      
      await debugLog('PocketBase: Mapped advertisements count:', mappedRecords.length);
      
      return { data: mappedRecords, error: null };
    } catch (error: any) {
      await debugLog('PocketBase: Error fetching advertisements:', error);
      await debugLog('PocketBase: Error details:', error?.data);
      return { data: null, error: error.message };
    }
  }

  static async getAdvertisement(id: string): Promise<{ data: Advertisement | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Fetching single advertisement:', id);
      const record = await pb.collection('advertisements').getOne(id);
      
      const deliveryMethods = parseDeliveryMethods(record.promocode_delivery_methods);

      // Construct banner URL - prefer uploaded file, fall back to image_url
      // Use getCacheableUrl to add version parameter for browser caching
      let bannerUrl = record.image_url || '';
      if (record.banner) {
        const rawUrl = pb.files.getURL(record, record.banner);
        bannerUrl = getCacheableUrl(rawUrl, record.updated);
      }

      // Construct provider logo URL if uploaded file exists
      let providerLogoUrl = record.provider_logo_url || '';
      if (record.provider_logo) {
        const rawProviderLogoUrl = pb.files.getURL(record, record.provider_logo);
        providerLogoUrl = getCacheableUrl(rawProviderLogoUrl, record.updated);
      }

      const advertisement: Advertisement = {
        id: record.id,
        name: record.title,
        description: record.description,
        start_date: record.start_date || '',
        end_date: record.end_date || '',
        banner_url: bannerUrl,
        partner_id: record.partner_id,
        status: record.status,
        approval_status: record.approval_status || undefined,
        priority: record.priority || 'bronze',
        advertisement_mode: record.advertisement_mode || 'default',
        categories: record.category ? (typeof record.category === 'string' && record.category.startsWith('[') ? JSON.parse(record.category) : [record.category]) : [],
        region: record.region,
        created_by: record.created_by || '',
        created_at: record.created,
        approved_by: record.approved_by || undefined,
        approved_at: record.approved_at || undefined,
        rejection_reason: record.rejection_reason || undefined,
        // promocode_config removed - use getPromocodeConfig() for admin access
        has_promocode: record.has_promocode || false,
        promocode_type: record.promocode_type || undefined,
        button_text: record.button_text || '',
        badge_text: record.badge_text_ || '', // Note: PocketBase field has underscore
        badge_color: record.badge_color || 'red',
        promocode_delivery_methods: deliveryMethods,
        target_url: record.target_url || '',
        target_url_enabled: record.target_url_enabled || false,
        // Provider information
        provider_entity_id: record.provider_entity_id || undefined,
        provider_name: record.provider_name || undefined,
        provider_logo: record.provider_logo || undefined,
        provider_logo_url: providerLogoUrl || undefined,
        provider_privacy_policy_url: record.provider_privacy_policy_url || undefined
      };
      
      await debugLog('PocketBase: Fetched advertisement:', advertisement);
      return { data: advertisement, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error fetching advertisement:', error);
      return { data: null, error: error.message };
    }
  }

  static async createAdvertisement(ad: Omit<Advertisement, 'id' | 'created_at'>, bannerFile?: File | null): Promise<{ data: Advertisement | null; error: string | null }> {
    try {
      await debugLog('Creating advertisement with data:', ad);
      // Note: promocode_config should be saved separately via savePromocodeConfig()
      
      // Ensure categories is an array
      await debugLog('🔍 Raw categories:', ad.categories);
      await debugLog('🔍 Categories type:', typeof ad.categories);
      await debugLog('🔍 Categories is array?', Array.isArray(ad.categories));
      
      let categoriesJson = '[]';
      if (ad.categories) {
        if (Array.isArray(ad.categories)) {
          categoriesJson = JSON.stringify(ad.categories);
        } else {
          console.warn('⚠️ Categories is not an array, converting:', ad.categories);
          categoriesJson = JSON.stringify([ad.categories]);
        }
      }
      await debugLog('🔍 Stringified categories:', categoriesJson);
      
      // Map our interface fields to PocketBase schema
      const pbData: any = {
        partner_id: ad.partner_id,
        title: ad.name, // name -> title
        description: ad.description,
        image_url: ad.banner_url, // banner_url -> image_url
        target_url: ad.target_url_enabled && ad.target_url ? ad.target_url : '', // Only set if enabled
        target_url_enabled: ad.target_url_enabled || false, // Whether to show target URL link
        category: categoriesJson, // Use pre-validated JSON
        region: ad.region,
        status: ad.status,
        priority: ad.priority || 2, // Default to Medium priority
        advertisement_mode: ad.advertisement_mode || 'default', // Publication workflow mode
        start_date: ad.start_date || '', // Required field - must have value
        end_date: ad.end_date || '', // Required field - must have value
        clicks: 0,
        impressions: 0,
        // promocode_config removed - use savePromocodeConfig() separately
        has_promocode: ad.has_promocode || false,
        promocode_type: ad.promocode_type || null,
        created_by: ad.created_by || ad.partner_id, // Add created_by field
        button_text: ad.button_text || '', // Custom button text
        badge_text_: ad.badge_text || '', // Optional badge text (note: field has underscore in PocketBase)
        badge_color: ad.badge_color || 'red', // Badge color
        // Approval workflow fields
        approval_status: ad.approval_status || 'pending', // Default to pending
        approved_by: ad.approved_by || '', // Empty string if not approved
        approved_at: ad.approved_at || '', // Empty string if not approved
        rejection_reason: ad.rejection_reason || '', // Empty string if not rejected
        promocode_delivery_methods: serializeDeliveryMethods(ad.promocode_delivery_methods),
        // Provider information
        provider_entity_id: ad.provider_entity_id || null,
        provider_name: ad.provider_name || '',
        provider_logo_url: ad.provider_logo_url || '',
        provider_privacy_policy_url: ad.provider_privacy_policy_url || ''
      };
      
      await debugLog('Mapped PocketBase data:', pbData);
      await debugLog('PocketBase data types:', {
        partner_id: typeof pbData.partner_id,
        title: typeof pbData.title,
        category: typeof pbData.category,
        categoryValue: pbData.category
      });
      
      // Validate JSON fields before sending
      if (pbData.category && typeof pbData.category === 'string') {
        // Check if it looks like [object Object] before attempting parse
        if (pbData.category === '[object Object]' || pbData.category.includes('[object Object]')) {
          console.error('❌ Category contains [object Object]:', pbData.category);
          throw new Error('Category was not properly serialized - contains [object Object]');
        }
        try {
          JSON.parse(pbData.category);
          await debugLog('✅ Category JSON is valid');
        } catch (e) {
          console.error('❌ Invalid category JSON:', pbData.category);
          throw new Error('Category field is not valid JSON: ' + pbData.category);
        }
      }
      
      // Use FormData if there's a banner file to upload
      let record;
      if (bannerFile) {
        const formData = new FormData();
        Object.keys(pbData).forEach(key => {
          const value = pbData[key];
          if (value !== undefined && value !== null) {
            formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
          }
        });
        formData.append('banner', bannerFile);
        record = await pb.collection('advertisements').create(formData);
      } else {
        record = await pb.collection('advertisements').create(pbData);
      }
      await debugLog('Advertisement created successfully:', record);
      await debugLog('Record category type:', typeof record.category);
      await debugLog('Record category value:', record.category);
      
      // Parse categories safely - PocketBase might return it as object or string
      let categories: AdvertisementCategory[] = [];
      if (record.category) {
        if (typeof record.category === 'string') {
          try {
            categories = JSON.parse(record.category);
          } catch (e) {
            console.warn('Failed to parse category string, using as single item:', record.category);
            categories = [record.category as AdvertisementCategory];
          }
        } else if (Array.isArray(record.category)) {
          categories = record.category;
        } else {
          console.warn('Unexpected category type, using original:', record.category);
          categories = ad.categories;
        }
      }
      
      
      // Map PocketBase response back to our interface
      const deliveryMethods = parseDeliveryMethods(record.promocode_delivery_methods ?? ad.promocode_delivery_methods);

      // Construct banner URL - prefer uploaded file, fall back to image_url
      // Use getCacheableUrl to add version parameter for browser caching
      let bannerUrl = record.image_url || '';
      if (record.banner) {
        const rawUrl = pb.files.getURL(record, record.banner);
        bannerUrl = getCacheableUrl(rawUrl, record.updated);
      }

      const mappedData: Advertisement = {
        id: record.id,
        name: record.title,
        description: record.description,
        start_date: record.start_date || ad.start_date,
        end_date: record.end_date || ad.end_date,
        banner_url: bannerUrl,
        partner_id: record.partner_id,
        status: record.status,
        approval_status: record.approval_status || ad.approval_status || undefined,
        priority: record.priority || ad.priority || 'bronze',
        advertisement_mode: record.advertisement_mode || ad.advertisement_mode || 'default',
        categories: categories,
        region: record.region,
        created_by: record.created_by || ad.created_by,
        created_at: record.created,
        approved_by: record.approved_by || ad.approved_by || undefined,
        approved_at: record.approved_at || ad.approved_at || undefined,
        rejection_reason: record.rejection_reason || ad.rejection_reason || undefined,
        // promocode_config removed - use getPromocodeConfig() for admin access
        has_promocode: record.has_promocode || ad.has_promocode || false,
        promocode_type: record.promocode_type || ad.promocode_type || undefined,
        button_text: record.button_text || ad.button_text || '',
        badge_text: record.badge_text_ || ad.badge_text || '',
        badge_color: record.badge_color || ad.badge_color || 'red',
        promocode_delivery_methods: deliveryMethods,
        target_url: record.target_url || ad.target_url || '',
        target_url_enabled: record.target_url_enabled || ad.target_url_enabled || false
      };
      
      // Trigger webhook for advertisement_created event
      this.triggerWebhooks(record.partner_id, 'advertisement_created', {
        advertisement_id: record.id,
        advertisement_name: record.title,
        partner_id: record.partner_id,
        status: record.status,
        approval_status: record.approval_status,
        created_at: record.created
      }).catch(err => console.error('Failed to trigger webhooks:', err));
      
      // Trigger email notification for advertisement_created event
      notificationTriggerService.triggerEvent(
        record.partner_id,
        'advertisement_created',
        {
          ad_title: record.title,
          ad_name: record.title,
          ad_id: record.id,
          created_at: record.created,
          ad_image_url: record.image_url,
          ad_url: `${window.location.origin}/advertisements/${record.id}`
        }
      ).catch(err => console.error('Failed to trigger email notifications:', err));
      
      return { data: mappedData, error: null };
    } catch (error: any) {
      console.error('PocketBase createAdvertisement error:', error);
      console.error('Error type:', typeof error);
      console.error('Error message:', error.message);
      console.error('Error data:', error.data);
      console.error('Error response:', error.response);
      console.error('Error stack:', error.stack);
      
      // Try to extract more detailed error information
      let errorMessage = 'Failed to create advertisement';
      if (error.data) {
        try {
          errorMessage = JSON.stringify(error.data, null, 2);
        } catch (e) {
          errorMessage = String(error.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data) {
        try {
          errorMessage = JSON.stringify(error.response.data, null, 2);
        } catch (e) {
          errorMessage = String(error.response.data);
        }
      }
      
      console.error('Final error message:', errorMessage);
      return { data: null, error: errorMessage };
    }
  }

  static async updateAdvertisement(id: string, updates: Partial<Advertisement>, bannerFile?: File | null): Promise<{ data: Advertisement | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Updating advertisement:', id, 'with updates:', updates);
      
      // Map our interface fields to PocketBase schema
      const pbUpdates: any = {};
      if (updates.name !== undefined) pbUpdates.title = updates.name;
      if (updates.description !== undefined) pbUpdates.description = updates.description;
      if (updates.banner_url !== undefined) {
        pbUpdates.image_url = updates.banner_url;
      }
      if (updates.categories !== undefined) pbUpdates.category = JSON.stringify(updates.categories);
      if (updates.region !== undefined) pbUpdates.region = updates.region;
      if (updates.status !== undefined) pbUpdates.status = updates.status;
      if (updates.partner_id !== undefined) pbUpdates.partner_id = updates.partner_id;
      if (updates.priority !== undefined) pbUpdates.priority = updates.priority; // Add priority
      if (updates.advertisement_mode !== undefined) pbUpdates.advertisement_mode = updates.advertisement_mode; // Add advertisement_mode
      if (updates.start_date !== undefined) pbUpdates.start_date = updates.start_date; // Add start_date
      if (updates.end_date !== undefined) pbUpdates.end_date = updates.end_date; // Add end_date
      if (updates.created_by !== undefined) pbUpdates.created_by = updates.created_by; // Add created_by
      // promocode_config removed - use savePromocodeConfig() separately
      if (updates.has_promocode !== undefined) pbUpdates.has_promocode = updates.has_promocode;
      if (updates.promocode_type !== undefined) pbUpdates.promocode_type = updates.promocode_type;
      if (updates.promocode_delivery_methods !== undefined) {
        pbUpdates.promocode_delivery_methods = serializeDeliveryMethods(updates.promocode_delivery_methods);
      }
      
      // Add new button and badge fields
      if (updates.button_text !== undefined) pbUpdates.button_text = updates.button_text;
      if (updates.badge_text !== undefined) pbUpdates.badge_text_ = updates.badge_text; // Note: PocketBase field has underscore
      if (updates.badge_color !== undefined) pbUpdates.badge_color = updates.badge_color;
      
      // Add target URL fields
      if (updates.target_url_enabled !== undefined) {
        pbUpdates.target_url_enabled = updates.target_url_enabled;
        // Only set target_url if enabled and provided
        if (updates.target_url_enabled && updates.target_url) {
          pbUpdates.target_url = updates.target_url;
        } else if (!updates.target_url_enabled) {
          pbUpdates.target_url = ''; // Clear target_url when disabled
        }
      }
      if (updates.target_url !== undefined && updates.target_url_enabled) {
        pbUpdates.target_url = updates.target_url;
      }
      
      // Add approval fields
      if (updates.approval_status !== undefined) pbUpdates.approval_status = updates.approval_status;
      if (updates.approved_by !== undefined) pbUpdates.approved_by = updates.approved_by;
      if (updates.approved_at !== undefined) pbUpdates.approved_at = updates.approved_at;
      if (updates.rejection_reason !== undefined) pbUpdates.rejection_reason = updates.rejection_reason;
      
      // Add provider fields
      if (updates.provider_entity_id !== undefined) pbUpdates.provider_entity_id = updates.provider_entity_id || null;
      if (updates.provider_name !== undefined) pbUpdates.provider_name = updates.provider_name || '';
      if (updates.provider_logo_url !== undefined) pbUpdates.provider_logo_url = updates.provider_logo_url || '';
      if (updates.provider_privacy_policy_url !== undefined) pbUpdates.provider_privacy_policy_url = updates.provider_privacy_policy_url || '';
      
      await debugLog('PocketBase: Sending update data:', pbUpdates);
      
      // Use FormData if there's a banner file to upload
      let record;
      if (bannerFile) {
        const formData = new FormData();
        Object.keys(pbUpdates).forEach(key => {
          const value = pbUpdates[key];
          if (value !== undefined && value !== null) {
            formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
          }
        });
        formData.append('banner', bannerFile);
        record = await pb.collection('advertisements').update(id, formData);
      } else {
        record = await pb.collection('advertisements').update(id, pbUpdates);
      }
      await debugLog('PocketBase: Update successful:', record);
      
      // Map response back to our interface
      const deliveryMethods = parseDeliveryMethods(record.promocode_delivery_methods ?? updates.promocode_delivery_methods);

      // Construct banner URL - prefer uploaded file, fall back to image_url
      // Use getCacheableUrl to add version parameter for browser caching
      let bannerUrl = record.image_url || '';
      if (record.banner) {
        const rawUrl = pb.files.getURL(record, record.banner);
        bannerUrl = getCacheableUrl(rawUrl, record.updated);
      }

      // Construct provider logo URL if uploaded file exists
      let providerLogoUrl = record.provider_logo_url || '';
      if (record.provider_logo) {
        const rawProviderLogoUrl = pb.files.getURL(record, record.provider_logo);
        providerLogoUrl = getCacheableUrl(rawProviderLogoUrl, record.updated);
      }

      const mappedData: Advertisement = {
        id: record.id,
        name: record.title,
        description: record.description,
        start_date: record.start_date || updates.start_date || '', // Read from DB
        end_date: record.end_date || updates.end_date || '', // Read from DB
        banner_url: bannerUrl,
        partner_id: record.partner_id,
        status: record.status,
        approval_status: record.approval_status || updates.approval_status || undefined,
        priority: record.priority || updates.priority || 'bronze', // Default to bronze priority
        advertisement_mode: record.advertisement_mode || updates.advertisement_mode || 'default',
        categories: record.category ? (typeof record.category === 'string' && record.category.startsWith('[') ? JSON.parse(record.category) : [record.category]) : [],
        region: record.region,
        created_by: record.created_by || updates.created_by || '', // Read from DB
        created_at: record.created,
        approved_by: record.approved_by || updates.approved_by || undefined,
        approved_at: record.approved_at || updates.approved_at || undefined,
        rejection_reason: record.rejection_reason || updates.rejection_reason || undefined,
        // promocode_config removed - use getPromocodeConfig() for admin access
        has_promocode: record.has_promocode || updates.has_promocode || false,
        promocode_type: record.promocode_type || updates.promocode_type || undefined,
        button_text: record.button_text || updates.button_text || '',
        badge_text: record.badge_text_ || updates.badge_text || '', // Note: PocketBase field has underscore
        badge_color: record.badge_color || updates.badge_color || 'red',
        promocode_delivery_methods: deliveryMethods,
        target_url: record.target_url || updates.target_url || '',
        target_url_enabled: record.target_url_enabled || updates.target_url_enabled || false,
        // Provider information
        provider_entity_id: record.provider_entity_id || updates.provider_entity_id || undefined,
        provider_name: record.provider_name || updates.provider_name || undefined,
        provider_logo: record.provider_logo || updates.provider_logo || undefined,
        provider_logo_url: providerLogoUrl || updates.provider_logo_url || undefined,
        provider_privacy_policy_url: record.provider_privacy_policy_url || updates.provider_privacy_policy_url || undefined
      };
      
      // Trigger webhooks based on what was updated
      if (updates.approval_status === 'approved') {
        this.triggerWebhooks(record.partner_id, 'advertisement_approved', {
          advertisement_id: record.id,
          advertisement_name: record.title,
          partner_id: record.partner_id,
          approved_by: record.approved_by,
          approved_at: record.approved_at
        }).catch(err => console.error('Failed to trigger webhooks:', err));
      } else if (updates.approval_status === 'rejected') {
        this.triggerWebhooks(record.partner_id, 'advertisement_rejected', {
          advertisement_id: record.id,
          advertisement_name: record.title,
          partner_id: record.partner_id,
          rejection_reason: record.rejection_reason
        }).catch(err => console.error('Failed to trigger webhooks:', err));
      } else if (updates.approval_status === undefined) {
        // General update (not approval-related)
        this.triggerWebhooks(record.partner_id, 'advertisement_updated', {
          advertisement_id: record.id,
          advertisement_name: record.title,
          partner_id: record.partner_id,
          status: record.status,
          updated_at: record.updated
        }).catch(err => console.error('Failed to trigger webhooks:', err));
      }
      
      return { data: mappedData, error: null };
    } catch (error: any) {
      console.error('PocketBase: Update advertisement error:', error);
      console.error('PocketBase: Error data:', error.data);
      const errorMessage = error.data ? JSON.stringify(error.data) : error.message;
      return { data: null, error: errorMessage };
    }
  }

  static async deleteAdvertisement(id: string): Promise<{ error: string | null }> {
    try {
      // Get advertisement details before deleting for webhook
      const ad = await pb.collection('advertisements').getOne(id);
      
      await pb.collection('advertisements').delete(id);
      
      // Trigger webhook for advertisement_deleted event
      this.triggerWebhooks(ad.partner_id, 'advertisement_deleted', {
        advertisement_id: ad.id,
        advertisement_name: ad.title,
        partner_id: ad.partner_id,
        deleted_at: new Date().toISOString()
      }).catch(err => console.error('Failed to trigger webhooks:', err));
      
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }

  // Partner Campaigns
  static async getPartnerCampaigns(): Promise<{ data: PartnerCampaign[] | null; error: string | null }> {
    try {
      const records = await pb.collection('partner_campaigns').getFullList({
        sort: '-created',
      });
      
      // Map PocketBase records to PartnerCampaign interface
      const campaigns: PartnerCampaign[] = records.map(record => ({
        id: record.id,
        name: record.name,
        description: record.description,
        partner_id: record.partner_id,
        priority: record.priority,
        status: record.status,
        advertisement_ids: Array.isArray(record.advertisement_ids)
          ? record.advertisement_ids
          : (record.advertisement_ids ? JSON.parse(record.advertisement_ids) : []),
        region: record.region,
        created_by: record.created_by || record.partner_id, // Use partner_id as fallback
        created_at: record.created // Map PocketBase 'created' to 'created_at'
      }));
      
      return { data: campaigns, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async createPartnerCampaign(campaign: Omit<PartnerCampaign, 'id' | 'created_at'>): Promise<{ data: PartnerCampaign | null; error: string | null }> {
    try {
      const record = await pb.collection('partner_campaigns').create(campaign);
      
      // Map PocketBase record to PartnerCampaign interface
      const mappedCampaign: PartnerCampaign = {
        id: record.id,
        name: record.name,
        description: record.description,
        partner_id: record.partner_id,
        priority: record.priority,
        status: record.status,
        advertisement_ids: Array.isArray(record.advertisement_ids)
          ? record.advertisement_ids
          : (record.advertisement_ids ? JSON.parse(record.advertisement_ids) : []),
        region: record.region,
        created_by: record.created_by || record.partner_id,
        created_at: record.created
      };
      
      // Trigger webhook for campaign_created event
      this.triggerWebhooks(record.partner_id, 'campaign_created', {
        campaign_id: record.id,
        campaign_name: record.name,
        partner_id: record.partner_id,
        status: record.status,
        region: record.region,
        created_at: record.created
      }).catch(err => console.error('Failed to trigger webhooks:', err));
      
      // Trigger email notification for campaign_created event
      notificationTriggerService.triggerEvent(
        record.partner_id,
        'campaign_created',
        {
          campaign_name: record.name,
          campaign_id: record.id,
          partner_name: 'Partner', // Will be expanded in email template
          status: record.status,
          description: record.description,
          created_at: record.created,
          campaign_url: `${window.location.origin}/campaigns/${record.id}`
        }
      ).catch(err => console.error('Failed to trigger email notifications:', err));
      
      return { data: mappedCampaign, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async updatePartnerCampaign(id: string, updates: Partial<PartnerCampaign>): Promise<{ data: PartnerCampaign | null; error: string | null }> {
    try {
      // Get campaign before update to check status change
      const oldRecord = await pb.collection('partner_campaigns').getOne(id);
      const record = await pb.collection('partner_campaigns').update(id, updates);
      
      // Map PocketBase record to PartnerCampaign interface
      const mappedCampaign: PartnerCampaign = {
        id: record.id,
        name: record.name,
        description: record.description,
        partner_id: record.partner_id,
        priority: record.priority,
        status: record.status,
        advertisement_ids: Array.isArray(record.advertisement_ids)
          ? record.advertisement_ids
          : (record.advertisement_ids ? JSON.parse(record.advertisement_ids) : []),
        region: record.region,
        created_by: record.created_by || record.partner_id,
        created_at: record.created
      };
      
      // Trigger webhooks based on status change
      if (oldRecord.status !== record.status && record.status === 'completed') {
        this.triggerWebhooks(record.partner_id, 'campaign_completed', {
          campaign_id: record.id,
          campaign_name: record.name,
          partner_id: record.partner_id,
          completed_at: record.updated
        }).catch(err => console.error('Failed to trigger webhooks:', err));
      } else {
        this.triggerWebhooks(record.partner_id, 'campaign_updated', {
          campaign_id: record.id,
          campaign_name: record.name,
          partner_id: record.partner_id,
          status: record.status,
          updated_at: record.updated
        }).catch(err => console.error('Failed to trigger webhooks:', err));
      }
      
      // Trigger email notifications
      if (oldRecord.status !== record.status) {
        // Status changed - trigger campaign_status_updates
        notificationTriggerService.triggerEvent(
          record.partner_id,
          'campaign_status_updates',
          {
            campaign_name: record.name,
            campaign_id: record.id,
            status: record.status,
            previous_status: oldRecord.status,
            updated_at: record.updated,
            campaign_url: `${window.location.origin}/campaigns/${record.id}`
          }
        ).catch(err => console.error('Failed to trigger email notifications:', err));
      } else {
        // General update - trigger campaign_updated
        const changes: string[] = [];
        if (oldRecord.name !== record.name) changes.push(`Name changed to "${record.name}"`);
        if (oldRecord.description !== record.description) changes.push('Description updated');
        if (oldRecord.priority !== record.priority) changes.push(`Priority changed to ${record.priority}`);
        if (oldRecord.region !== record.region) changes.push(`Region changed to ${record.region}`);
        
        notificationTriggerService.triggerEvent(
          record.partner_id,
          'campaign_updated',
          {
            campaign_name: record.name,
            campaign_id: record.id,
            updated_by: 'System',
            updated_at: record.updated,
            changes: changes,
            campaign_url: `${window.location.origin}/campaigns/${record.id}`
          }
        ).catch(err => console.error('Failed to trigger email notifications:', err));
      }
      
      return { data: mappedCampaign, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async deletePartnerCampaign(id: string): Promise<{ error: string | null }> {
    try {
      // Get campaign details before deleting for webhook
      const campaign = await pb.collection('partner_campaigns').getOne(id);
      
      await pb.collection('partner_campaigns').delete(id);
      
      // Trigger webhook for campaign_deleted event
      this.triggerWebhooks(campaign.partner_id, 'campaign_deleted', {
        campaign_id: campaign.id,
        campaign_name: campaign.name,
        partner_id: campaign.partner_id,
        deleted_at: new Date().toISOString()
      }).catch(err => console.error('Failed to trigger webhooks:', err));
      
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }

  // Marketing Campaigns
  static async getMarketingCampaigns(): Promise<{ data: MarketingCampaign[] | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Fetching marketing campaigns...');
      const records = await pb.collection('marketing_campaigns').getFullList({
        sort: '-created',
      });
      
      await debugLog('PocketBase: Found marketing campaigns:', records.length);
      await debugLog('PocketBase: Raw marketing campaign records:', records);
      
      // Map PocketBase records to MarketingCampaign interface
      const campaigns: MarketingCampaign[] = records.map(record => ({
        id: record.id,
        name: record.name,
        description: record.description,
        status: record.status,
        partner_campaign_ids: Array.isArray(record.partner_campaign_ids) 
          ? record.partner_campaign_ids 
          : (record.partner_campaign_ids ? JSON.parse(record.partner_campaign_ids) : []),
        region: record.region,
        locale: record.locale,
        created_by: record.created_by,
        created_at: record.created, // Map PocketBase 'created' to 'created_at'
        approved_at: record.approved_at
      }));
      
      await debugLog('PocketBase: Mapped marketing campaigns:', campaigns.length);
      
      return { data: campaigns, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error fetching marketing campaigns:', error);
      return { data: null, error: error.message };
    }
  }

  static async createMarketingCampaign(campaign: Omit<MarketingCampaign, 'id' | 'created_at'>): Promise<{ data: MarketingCampaign | null; error: string | null }> {
    try {
      const record = await pb.collection('marketing_campaigns').create(campaign);
      
      // Map PocketBase record to MarketingCampaign interface
      const mappedCampaign: MarketingCampaign = {
        id: record.id,
        name: record.name,
        description: record.description,
        status: record.status,
        partner_campaign_ids: Array.isArray(record.partner_campaign_ids) 
          ? record.partner_campaign_ids 
          : (record.partner_campaign_ids ? JSON.parse(record.partner_campaign_ids) : []),
        region: record.region,
        locale: record.locale,
        created_by: record.created_by,
        created_at: record.created,
        approved_at: record.approved_at
      };
      
      return { data: mappedCampaign, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async updateMarketingCampaign(id: string, updates: Partial<MarketingCampaign>): Promise<{ data: MarketingCampaign | null; error: string | null }> {
    try {
      const record = await pb.collection('marketing_campaigns').update(id, updates);
      
      // Map PocketBase record to MarketingCampaign interface
      const mappedCampaign: MarketingCampaign = {
        id: record.id,
        name: record.name,
        description: record.description,
        status: record.status,
        partner_campaign_ids: Array.isArray(record.partner_campaign_ids) 
          ? record.partner_campaign_ids 
          : (record.partner_campaign_ids ? JSON.parse(record.partner_campaign_ids) : []),
        region: record.region,
        locale: record.locale,
        created_by: record.created_by,
        created_at: record.created,
        approved_at: record.approved_at
      };
      
      return { data: mappedCampaign, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async deleteMarketingCampaign(id: string): Promise<{ error: string | null }> {
    try {
      await pb.collection('marketing_campaigns').delete(id);
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }

  // Promo Code Requests
  static async getPromoRequests(): Promise<{ data: PromoCodeRequest[] | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Fetching promo requests...');
      const records = await pb.collection('promo_requests').getFullList({
        sort: '-created',
      });
      
      await debugLog('PocketBase: Found promo requests:', records.length);
      if (records[0]) {
        await debugLog('PocketBase: Raw promo request sample:', records[0]);
      }
      
      // Map PocketBase field names to our interface
      const mappedRecords: PromoCodeRequest[] = [];
      for (const record of records) {
        await debugLog('Mapping promo request:', {
          id: record.id,
          user_email: record.user_email,
          partner_campaign_id: record.partner_campaign_id,
          partner_id: record.partner_id,
          advertisement_id: record.advertisement_id,
          allFields: Object.keys(record)
        });
        
        mappedRecords.push({
          id: record.id,
          email: record.user_email,
          partner_campaign_id: record.partner_campaign_id,
          partner_id: record.partner_id,
          advertisement_id: record.advertisement_id,
          advertisement_name: record.advertisement_name,
          campaign_name: record.campaign_name,
          marketing_campaign_id: record.marketing_campaign_id,
          promo_code: record.promo_code,
          agreed_to_privacy: record.agreed_to_privacy,
          requested_at: record.created,
          status: record.status
        });
      }
      
      await debugLog('PocketBase: Mapped promo requests:', mappedRecords.length);
      
      return { data: mappedRecords, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error fetching promo requests:', error);
      return { data: null, error: error.message };
    }
  }

  static async createPromoRequest(request: Omit<PromoCodeRequest, 'id' | 'requested_at'>): Promise<{ data: PromoCodeRequest | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Creating promo request with data:', request);
      
      // Validate required fields
      if (!request.advertisement_id) {
        throw new Error('advertisement_id is required');
      }
      if (!request.email) {
        throw new Error('email is required');
      }
      // partner_campaign_id is optional for independent/universal ads accessed via direct link
      if (!request.partner_id) {
        throw new Error('partner_id is required');
      }
      
      // Map our field names to PocketBase field names
      const pocketbaseData = {
        user_email: request.email,
        user_name: request.email.split('@')[0], // Extract name from email as fallback
        partner_campaign_id: request.partner_campaign_id,
        partner_id: request.partner_id,
        advertisement_id: request.advertisement_id,
        advertisement_name: request.advertisement_name || 'Advertisement',
        campaign_name: request.campaign_name || '',
        marketing_campaign_id: request.marketing_campaign_id || '',
        promo_code: request.promo_code || '',
        agreed_to_privacy: request.agreed_to_privacy,
        status: request.status || 'pending'
      };
      
      await debugLog('PocketBase: Mapped data for PocketBase:', pocketbaseData);
      
      // Log each field individually for debugging
      await debugLog('PocketBase: Field by field check:');
      for (const [key, value] of Object.entries(pocketbaseData)) {
        await debugLog(`  ${key}: ${value} (${typeof value})`);
      }
      
      const record = await pb.collection('promo_requests').create(pocketbaseData);
      await debugLog('PocketBase: Promo request created successfully:', record);
      
      // Map back to our interface
      const mappedRecord: PromoCodeRequest = {
        id: record.id,
        email: record.user_email,
        partner_campaign_id: record.partner_campaign_id,
        partner_id: record.partner_id,
        advertisement_id: record.advertisement_id,
        advertisement_name: record.advertisement_name,
        campaign_name: record.campaign_name,
        marketing_campaign_id: record.marketing_campaign_id,
        promo_code: record.promo_code,
        agreed_to_privacy: record.agreed_to_privacy,
        requested_at: record.created,
        status: record.status
      };
      
      // Trigger webhooks for promo_code_requested event
      if (record.partner_id) {
        this.triggerWebhooks(record.partner_id, 'promo_code_requested', {
          promo_request_id: record.id,
          email: record.user_email,
          partner_campaign_id: record.partner_campaign_id,
          advertisement_id: record.advertisement_id,
          advertisement_name: record.advertisement_name,
          promo_code: record.promo_code,
          status: record.status,
          requested_at: record.created
        }).catch(err => console.error('Failed to trigger webhooks:', err));
        
        // Note: Email notifications are now handled by server-side PocketBase hooks
        // See pb_hooks/email_notifications.pb.js
        await debugLog('✅ Promo request created. Email notifications handled by server-side hook.');
      } else {
        console.error('⚠️ Cannot trigger webhook: partner_id is missing from promo request', record);
      }
      
      return { data: mappedRecord, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error creating promo request:', error);
      console.error('PocketBase: Error details:', error.response);
      console.error('PocketBase: Error data:', error.data);
      console.error('PocketBase: Full error object:', JSON.stringify(error.data, null, 2));
      
      // Extract field-specific errors if available
      if (error.data?.data) {
        console.error('PocketBase: Field validation errors:', error.data.data);
      }
      
      return { data: null, error: error.message || JSON.stringify(error.data) };
    }
  }

  // Bulk delete promo requests with filters
  static async deletePromoRequestsBulk(options: {
    partnerId?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ deletedCount: number; error: string | null }> {
    try {
      await debugLog('PocketBase: Bulk deleting promo requests with options:', options);
      
      // Build filter string
      const filters: string[] = [];
      
      if (options.partnerId) {
        filters.push(`partner_id = "${options.partnerId}"`);
      }
      
      if (options.dateFrom) {
        filters.push(`created >= "${options.dateFrom}"`);
      }
      
      if (options.dateTo) {
        // Add one day to include the entire end date
        const endDate = new Date(options.dateTo);
        endDate.setDate(endDate.getDate() + 1);
        filters.push(`created < "${endDate.toISOString().split('T')[0]}"`);
      }
      
      const filterString = filters.length > 0 ? filters.join(' && ') : '';
      
      await debugLog('PocketBase: Filter string:', filterString);
      
      // First, get all matching records
      const records = await pb.collection('promo_requests').getFullList({
        filter: filterString || undefined,
      });
      
      await debugLog(`PocketBase: Found ${records.length} promo requests to delete`);
      
      // Delete each record
      let deletedCount = 0;
      for (const record of records) {
        try {
          await pb.collection('promo_requests').delete(record.id);
          deletedCount++;
        } catch (err) {
          console.error(`Failed to delete promo request ${record.id}:`, err);
        }
      }
      
      await debugLog(`PocketBase: Successfully deleted ${deletedCount} promo requests`);
      
      return { deletedCount, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error bulk deleting promo requests:', error);
      return { deletedCount: 0, error: error.message };
    }
  }

  // Count promo requests matching filters (for preview before deletion)
  static async countPromoRequests(options: {
    partnerId?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ count: number; error: string | null }> {
    try {
      // Build filter string
      const filters: string[] = [];
      
      if (options.partnerId) {
        filters.push(`partner_id = "${options.partnerId}"`);
      }
      
      if (options.dateFrom) {
        filters.push(`created >= "${options.dateFrom}"`);
      }
      
      if (options.dateTo) {
        const endDate = new Date(options.dateTo);
        endDate.setDate(endDate.getDate() + 1);
        filters.push(`created < "${endDate.toISOString().split('T')[0]}"`);
      }
      
      const filterString = filters.length > 0 ? filters.join(' && ') : '';
      
      const result = await pb.collection('promo_requests').getList(1, 1, {
        filter: filterString || undefined,
      });
      
      return { count: result.totalItems, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error counting promo requests:', error);
      return { count: 0, error: error.message };
    }
  }

  static async updatePromoRequest(id: string, updates: Partial<PromoCodeRequest>): Promise<{ data: PromoCodeRequest | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Updating promo request:', id, updates);
      
      const pocketbaseData: any = {};
      if (updates.status) pocketbaseData.status = updates.status;
      if (updates.promo_code !== undefined) pocketbaseData.promo_code = updates.promo_code;
      
      const record = await pb.collection('promo_requests').update(id, pocketbaseData);
      
      const mappedRecord: PromoCodeRequest = {
        id: record.id,
        email: record.user_email,
        partner_campaign_id: record.partner_campaign_id,
        partner_id: record.partner_id,
        advertisement_id: record.advertisement_id,
        advertisement_name: record.advertisement_name,
        marketing_campaign_id: record.marketing_campaign_id,
        promo_code: record.promo_code,
        agreed_to_privacy: record.agreed_to_privacy,
        requested_at: record.created,
        status: record.status
      };
      
      // Trigger webhooks for promo code status changes
      if (updates.status === 'promo_provided' || updates.status === 'sent') {
        this.triggerWebhooks(record.partner_id, 'promo_code_approved', {
          promo_request_id: record.id,
          email: record.user_email,
          promo_code: record.promo_code,
          status: record.status
        }).catch(err => console.error('Failed to trigger webhooks:', err));
      } else if (updates.status === 'declined') {
        this.triggerWebhooks(record.partner_id, 'promo_code_rejected', {
          promo_request_id: record.id,
          email: record.user_email,
          status: record.status
        }).catch(err => console.error('Failed to trigger webhooks:', err));
      }
      
      return { data: mappedRecord, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error updating promo request:', error);
      return { data: null, error: error.message };
    }
  }

  // Contact Inquiries
  static async getContactInquiries(): Promise<{ data: ContactInquiry[] | null; error: string | null }> {
    try {
      const records = await pb.collection('contact_inquiries').getFullList({
        sort: '-created',
      });
      // Map PocketBase system fields to our ContactInquiry shape
      const inquiries: ContactInquiry[] = records.map((record: any) => ({
        id: record.id,
        name: record.name,
        email: record.email,
        subject: record.subject,
        message: record.message,
        status: record.status,
        priority: record.priority,
        created_at: record.created,
        updated_at: record.updated,
        // PocketBase field is 'notes', map it to admin_notes for the UI
        admin_notes: (record as any).notes,
        closed_at: (record as any).closed_at ?? null,
      }));

      return { data: inquiries, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async createContactInquiry(inquiry: Omit<ContactInquiry, 'id' | 'created_at' | 'updated_at' | 'status' | 'priority'>): Promise<{ data: ContactInquiry | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Submitting contact inquiry via secure endpoint');
      
      const response = await pb.send('/api/contact/submit', {
        method: 'POST',
        body: JSON.stringify({
          name: inquiry.name,
          email: inquiry.email,
          subject: inquiry.subject,
          message: inquiry.message,
          recaptcha_token: inquiry.recaptcha_token
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      await debugLog('PocketBase: Contact inquiry response:', response);
      
      if (response.success) {
        // Return a partial ContactInquiry object with the ID
        return { 
          data: {
            id: response.inquiry_id,
            name: inquiry.name,
            email: inquiry.email,
            subject: inquiry.subject,
            message: inquiry.message,
            status: 'new',
            priority: 'medium',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            closed_at: null,
          } as ContactInquiry, 
          error: null 
        };
      } else {
        return { data: null, error: response.error || 'Failed to submit inquiry' };
      }
    } catch (error: any) {
      console.error('PocketBase: Error submitting contact inquiry:', error);
      
      let errorMessage = 'Failed to submit inquiry';
      if (error.data?.error) {
        errorMessage = error.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { data: null, error: errorMessage };
    }
  }

  static async updateContactInquiry(id: string, updates: Partial<ContactInquiry>): Promise<{ data: ContactInquiry | null; error: string | null }> {
    try {
      // Map our admin_notes to PocketBase 'notes' field on update
      const payload: any = { ...updates };
      if (typeof updates.admin_notes !== 'undefined') {
        payload.notes = updates.admin_notes;
        delete payload.admin_notes;
      }

      const record: any = await pb.collection('contact_inquiries').update(id, payload);

      // Normalize to ContactInquiry shape so UI always has created_at/updated_at/closed_at
      const inquiry: ContactInquiry = {
        id: record.id,
        name: record.name,
        email: record.email,
        subject: record.subject,
        message: record.message,
        status: record.status,
        priority: record.priority,
        created_at: record.created,
        updated_at: record.updated,
        admin_notes: (record as any).notes,
        closed_at: (record as any).closed_at ?? null,
      };

      return { data: inquiry, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async deleteContactInquiry(id: string): Promise<{ error: string | null }> {
    try {
      await pb.collection('contact_inquiries').delete(id);
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }

  // Categories
  static async getCategories(): Promise<{ data: Category[] | null; error: string | null }> {
    try {
      const records = await pb.collection('categories').getFullList({
        sort: 'name',
      });
      return { data: records as unknown as Category[], error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async createCategory(category: Omit<Category, 'id' | 'created_at'>): Promise<{ data: Category | null; error: string | null }> {
    try {
      const record = await pb.collection('categories').create(category);
      return { data: record as unknown as Category, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async updateCategory(id: string, updates: Partial<Category>): Promise<{ data: Category | null; error: string | null }> {
    try {
      const record = await pb.collection('categories').update(id, updates);
      return { data: record as unknown as Category, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async deleteCategory(id: string): Promise<{ error: string | null }> {
    try {
      await pb.collection('categories').delete(id);
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }

  // Filters
  static async getFilters(): Promise<{ data: Filter[] | null; error: string | null }> {
    try {
      const records = await pb.collection('filters').getFullList({
        sort: 'name',
      });
      return { data: records as unknown as Filter[], error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async createFilter(filter: Omit<Filter, 'id' | 'created_at'>): Promise<{ data: Filter | null; error: string | null }> {
    try {
      const record = await pb.collection('filters').create(filter);
      return { data: record as unknown as Filter, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async updateFilter(id: string, updates: Partial<Filter>): Promise<{ data: Filter | null; error: string | null }> {
    try {
      const record = await pb.collection('filters').update(id, updates);
      return { data: record as unknown as Filter, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async deleteFilter(id: string): Promise<{ error: string | null }> {
    try {
      await pb.collection('filters').delete(id);
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }

  // Users (for partner admins)
  static async getPartnerAdmins(): Promise<{ data: any[] | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Fetching partner admins... [v2]');
      
      const records = await pb.collection('users').getFullList({
        filter: 'role = "partner_admin"',
        sort: '-created',
      });
      
      await debugLog('PocketBase: Raw records count:', records.length);
      await debugLog('PocketBase: Partner admins found:', records.length);
      
      // Map PocketBase fields to PartnerAdmin interface
      const partnerAdmins = records.map(record => ({
        id: record.id,
        name: record.name,
        email: record.email,
        partner_id: record.partner_id || '',
        status: record.status || 'active',
        last_login: record.last_login,
        first_login_completed: record.first_login_completed || false,
        initial_password: record.initial_password,
        created_at: record.created  // PocketBase uses 'created', we need 'created_at'
      }));
      
      await debugLog('PocketBase: Mapped partner admins count:', partnerAdmins.length);
      
      return { data: partnerAdmins, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error fetching partner admins:', error);
      console.error('PocketBase: Error details:', error.data);
      return { data: null, error: error.message };
    }
  }

  static async getGlobalAdmins(): Promise<{ data: any[] | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Fetching global admins...');
      
      const records = await pb.collection('users').getFullList({
        filter: 'role = "global_admin"',
        sort: '-created',
      });
      
      await debugLog('PocketBase: Global admins found:', records.length);
      
      // Map PocketBase fields to User interface
      const globalAdmins = records.map(record => ({
        id: record.id,
        name: record.name,
        email: record.email,
        role: record.role,
        partner_id: record.partner_id,
        created_at: record.created
      }));
      
      return { data: globalAdmins, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error fetching global admins:', error);
      return { data: null, error: error.message };
    }
  }

  static async updateUser(id: string, updates: any): Promise<{ error: string | null }> {
    try {
      await debugLog('Updating user:', id, 'with updates:', Object.keys(updates));
      await pb.collection('users').update(id, updates);
      await debugLog('User update successful');
      return { error: null };
    } catch (error: any) {
      console.error('User update error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      return { error: error.message };
    }
  }

  static async deleteUser(id: string): Promise<{ error: string | null }> {
    try {
      await pb.collection('users').delete(id);
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }

  // Activity Logs
  static async getActivityLogs(partnerId?: string): Promise<{ data: any[] | null; error: string | null }> {
    try {
      let filter = '';
      if (partnerId) {
        filter = `partner_id = "${partnerId}"`;
      }
      
      const records = await pb.collection('activity_logs').getFullList({
        sort: '-created',
        filter: filter,
      });
      
      return { data: records, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async createActivityLog(logData: any): Promise<{ data: any | null; error: string | null }> {
    try {
      const record = await pb.collection('activity_logs').create(logData);
      return { data: record, error: null };
    } catch (error: any) {
      console.error('Failed to create activity log:', error);
      return { data: null, error: error.message };
    }
  }

  // Webhook Triggering
  private static async triggerWebhooks(partnerId: string, eventType: string, eventData: any): Promise<void> {
    try {
      if (!partnerId) {
        console.error('⚠️ Cannot trigger webhooks: partnerId is undefined');
        return;
      }

      // Debug: Check all webhooks for this partner
      const allPartnerWebhooks = await pb.collection('partner_webhooks').getFullList({
        filter: `partner_id = "${partnerId}"`
      });
      await debugLog(`🔍 All webhooks for partner ${partnerId}:`, allPartnerWebhooks.length);
      if (allPartnerWebhooks.length > 0) {
        await debugLog('First webhook:', allPartnerWebhooks[0]);
      }

      // Find active webhooks for this partner
      const allWebhooks = await pb.collection('partner_webhooks').getFullList({
        filter: `partner_id = "${partnerId}" && enabled = true`
      });
      
      // Filter webhooks that subscribe to this event
      const webhooks = allWebhooks.filter(webhook => {
        try {
          const subscriptions = typeof webhook.event_subscriptions === 'string' 
            ? JSON.parse(webhook.event_subscriptions)
            : webhook.event_subscriptions;
          return Array.isArray(subscriptions) && subscriptions.includes(eventType);
        } catch (e) {
          console.error('Failed to parse webhook event_subscriptions:', e);
          return false;
        }
      });

      await debugLog(`🔔 Triggering ${eventType} webhooks for partner ${partnerId}:`, webhooks.length);
      
      if (webhooks.length === 0) {
        await debugLog(`ℹ️ No webhooks found for partner ${partnerId} subscribed to ${eventType}`);
        await debugLog(`   Filter used: partner_id = "${partnerId}" && enabled = true && ${eventType} = true`);
        return;
      }

      // Send webhook to each URL
      for (const webhook of webhooks) {
        const payload = {
          event: eventType,
          timestamp: new Date().toISOString(),
          partner_id: partnerId,
          data: eventData
        };

        try {
          const response = await fetch(webhook.webhook_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Webhook-Secret': webhook.secret_key || '',
              ...(webhook.auth_header ? { 'Authorization': webhook.auth_header } : {})
            },
            body: JSON.stringify(payload)
          });

          // Log delivery
          try {
            await pb.collection('webhook_delivery_logs').create({
              webhook_id: webhook.id,
              partner_id: partnerId,
              event_type: eventType,
              event_data: JSON.stringify(eventData),
              http_status: response.status,
              response_body: await response.text(),
              success: response.ok,
              attempt_number: 1
            });
          } catch (logError: any) {
            console.error('Failed to log webhook delivery:', logError);
          }

          await debugLog(`✅ Webhook delivered to ${webhook.webhook_url}: ${response.status}`);
        } catch (error: any) {
          console.error(`❌ Failed to deliver webhook to ${webhook.webhook_url}:`, error);
          
          // Log failed delivery
          try {
            await pb.collection('webhook_delivery_logs').create({
              webhook_id: webhook.id,
              partner_id: partnerId,
              event_type: eventType,
              event_data: JSON.stringify(eventData),
              http_status: 0,
              error_message: error.message,
              success: false,
              attempt_number: 1
            });
          } catch (logError: any) {
            console.error('Failed to log webhook delivery error:', logError);
          }
        }
      }
    } catch (error) {
      console.error('Failed to trigger webhooks:', error);
    }
  }

  // Advertisement Promocode Configs (Secure)
  static async getPromocodeConfig(advertisementId: string): Promise<{ data: any | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Fetching secure promocode config for ad:', advertisementId);
      const records = await pb.collection('advertisement_promocode_configs').getFullList({
        filter: `advertisement_id = "${advertisementId}"`,
      });
      
      if (records.length === 0) {
        return { data: null, error: null };
      }
      
      const record = records[0];
      
      // Parse JSON fields
      const config = {
        id: record.id,
        advertisement_id: record.advertisement_id,
        type: record.type,
        static_code: record.static_code || undefined,
        pool_codes: record.pool_codes ? (typeof record.pool_codes === 'string' ? JSON.parse(record.pool_codes) : record.pool_codes) : undefined,
        pool_usage_limit: record.pool_usage_limit || 1,
        used_codes: record.used_codes ? (typeof record.used_codes === 'string' ? JSON.parse(record.used_codes) : record.used_codes) : undefined,
        redirect_url: record.redirect_url || undefined,
        generation_rules: record.generation_rules ? (typeof record.generation_rules === 'string' ? JSON.parse(record.generation_rules) : record.generation_rules) : undefined,
        api_config: record.api_config ? (typeof record.api_config === 'string' ? JSON.parse(record.api_config) : record.api_config) : undefined,
        created: record.created,
        updated: record.updated
      };
      
      await debugLog('PocketBase: Fetched secure config:', config);
      return { data: config, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error fetching promocode config:', error);
      return { data: null, error: error.message };
    }
  }

  static async savePromocodeConfig(advertisementId: string, config: any): Promise<{ data: any | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Saving secure promocode config for ad:', advertisementId);
      
      // Check if config already exists
      const { data: existing } = await this.getPromocodeConfig(advertisementId);
      
      // Prepare data with JSON stringification
      const configData: any = {
        advertisement_id: advertisementId,
        type: config.type,
        static_code: config.static_code || null,
        pool_codes: config.pool_codes ? JSON.stringify(config.pool_codes) : null,
        pool_usage_limit: config.pool_usage_limit || 1,
        used_codes: config.used_codes ? JSON.stringify(config.used_codes) : null,
        redirect_url: config.redirect_url || null,
        generation_rules: config.generation_rules ? JSON.stringify(config.generation_rules) : null,
        api_config: config.api_config ? JSON.stringify(config.api_config) : null
      };
      
      let record;
      if (existing) {
        // Update existing config
        await debugLog('PocketBase: Updating existing config:', existing.id);
        record = await pb.collection('advertisement_promocode_configs').update(existing.id, configData);
      } else {
        // Create new config
        await debugLog('PocketBase: Creating new config');
        record = await pb.collection('advertisement_promocode_configs').create(configData);
      }
      
      await debugLog('PocketBase: Saved config successfully:', record.id);
      return { data: record, error: null };
    } catch (error: any) {
      console.error('PocketBase: Error saving promocode config:', error);
      return { data: null, error: error.message };
    }
  }

  static async deletePromocodeConfig(configId: string): Promise<{ error: string | null }> {
    try {
      await pb.collection('advertisement_promocode_configs').delete(configId);
      return { error: null };
    } catch (error: any) {
      console.error('PocketBase: Error deleting promocode config:', error);
      return { error: error.message };
    }
  }

  // Secure Promocode Request (uses server-side endpoint)
  static async requestPromocode(advertisementId: string, userEmail: string, agreedToPrivacy: boolean, recaptchaToken?: string | null): Promise<{ data: any | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Requesting promocode via secure endpoint');
      
      const requestBody: Record<string, any> = {
        advertisement_id: advertisementId,
        user_email: userEmail,
        agreed_to_privacy: agreedToPrivacy
      };
      
      // Include reCAPTCHA token if provided
      if (recaptchaToken) {
        requestBody.recaptcha_token = recaptchaToken;
      }
      
      const response = await pb.send('/api/promocodes/request', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      await debugLog('PocketBase: Promocode request response:', response);
      
      if (response.success) {
        return { data: response, error: null };
      } else {
        return { data: null, error: response.error || 'Failed to request promocode' };
      }
    } catch (error: any) {
      console.error('PocketBase: Error requesting promocode:', error);
      
      let errorMessage = 'Failed to request promocode';
      if (error.data?.error) {
        errorMessage = error.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { data: null, error: errorMessage };
    }
  }

  // =====================================================
  // Subscribers (Newsletter)
  // =====================================================

  static async getSubscribers(): Promise<{ data: Subscriber[] | null; error: string | null }> {
    try {
      const records = await pb.collection('subscribers').getFullList({
        sort: '-created',
      });
      
      // Map PocketBase records to Subscriber interface
      const subscribers: Subscriber[] = records.map(record => ({
        id: record.id,
        email: record.email,
        agreed_to_privacy: record.agreed_to_privacy,
        status: record.status,
        confirmed: record.confirmed,
        confirmation_token: record.confirmation_token,
        confirmed_at: record.confirmed_at,
        source: record.source,
        campaign_id: record.campaign_id,
        region: record.region,
        created_at: record.created, // Map PocketBase 'created' to 'created_at'
        updated_at: record.updated // Map PocketBase 'updated' to 'updated_at'
      }));
      
      return { data: subscribers, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async createSubscriber(subscriber: Omit<Subscriber, 'id' | 'created_at' | 'updated_at' | 'status' | 'confirmed' | 'confirmation_token' | 'confirmed_at'>): Promise<{ data: Subscriber | null; error: string | null }> {
    try {
      // Generate a unique confirmation token
      const confirmationToken = crypto.randomUUID();

      const payload = {
        email: subscriber.email,
        agreed_to_privacy: subscriber.agreed_to_privacy,
        status: 'active',
        confirmed: false,
        confirmation_token: confirmationToken,
        source: subscriber.source || '',
        campaign_id: subscriber.campaign_id || '',
        region: subscriber.region || ''
      };
      
      console.log('[Subscriber] Creating subscriber with payload:', payload);

      const record = await pb.collection('subscribers').create(payload);
      console.log('[Subscriber] Created successfully:', record);
      return { data: record as unknown as Subscriber, error: null };
    } catch (error: any) {
      console.error('[Subscriber] Create error:', error);
      console.error('[Subscriber] Error response:', error.response);
      console.error('[Subscriber] Error data:', JSON.stringify(error.response?.data, null, 2));
      
      // 400 error with empty data often means unique constraint violation
      if (error.status === 400 || error.response?.status === 400) {
        // Check for email-related errors (unique constraint, validation, etc.)
        const emailError = error.response?.data?.data?.email;
        if (emailError) {
          console.error('[Subscriber] Email error details:', JSON.stringify(emailError, null, 2));
          
          // Check various error codes for duplicate email
          if (emailError.code === 'validation_not_unique' || 
              emailError.code === 'validation_unique_constraint_violation' ||
              emailError.message?.includes('unique') ||
              emailError.message?.includes('already exists')) {
            return { data: null, error: 'This email is already subscribed.' };
          }
          
          // Return the specific email error message
          return { data: null, error: emailError.message || 'Invalid email address.' };
        }
        
        // Empty data with 400 status usually means unique constraint
        // PocketBase doesn't always return detailed error for unique violations
        const responseData = error.response?.data?.data;
        if (!responseData || Object.keys(responseData).length === 0) {
          return { data: null, error: 'This email is already subscribed.' };
        }
      }
      
      if (error.message?.includes('UNIQUE constraint failed')) {
        return { data: null, error: 'This email is already subscribed.' };
      }
      
      return { data: null, error: error.message || 'Failed to subscribe. Please try again.' };
    }
  }

  static async confirmSubscriber(token: string): Promise<{ data: { success: boolean; message: string } | null; error: string | null }> {
    try {
      // Use secure server-side endpoint for confirmation
      const response = await pb.send('/api/subscribers/confirm', {
        method: 'POST',
        body: JSON.stringify({ token }),
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.success) {
        return { data: response, error: null };
      } else {
        return { data: null, error: response.error || 'Failed to confirm subscription' };
      }
    } catch (error: any) {
      console.error('Confirmation error:', error);
      
      let errorMessage = 'Invalid or expired confirmation token.';
      if (error.data?.error) {
        errorMessage = error.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { data: null, error: errorMessage };
    }
  }

  static async updateSubscriber(id: string, updates: Partial<Subscriber>): Promise<{ data: Subscriber | null; error: string | null }> {
    try {
      const record = await pb.collection('subscribers').update(id, updates);
      return { data: record as unknown as Subscriber, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async deleteSubscriber(id: string): Promise<{ error: string | null }> {
    try {
      await pb.collection('subscribers').delete(id);
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }

  // ==================== ENTITIES ====================

  static async getEntities(partnerId?: string): Promise<{ data: Entity[] | null; error: string | null }> {
    try {
      const filter = partnerId ? `partner_id = "${partnerId}"` : '';
      const records = await pb.collection('entities').getFullList({
        filter,
        sort: '-created'
      });
      
      const entities: Entity[] = records.map((record: any) => ({
        id: record.id,
        name: record.name,
        logo: record.logo,
        logo_url: record.logo_url || (record.logo ? pb.files.getURL(record, record.logo) : undefined),
        privacy_policy_url: record.privacy_policy_url,
        comment: record.comment,
        partner_id: record.partner_id,
        created: record.created,
        updated: record.updated
      }));
      
      return { data: entities, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async getEntity(id: string): Promise<{ data: Entity | null; error: string | null }> {
    try {
      const record = await pb.collection('entities').getOne(id);
      
      const entity: Entity = {
        id: record.id,
        name: record.name,
        logo: record.logo,
        logo_url: record.logo_url || (record.logo ? pb.files.getURL(record, record.logo) : undefined),
        privacy_policy_url: record.privacy_policy_url,
        comment: record.comment,
        partner_id: record.partner_id,
        created: record.created,
        updated: record.updated
      };
      
      return { data: entity, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async createEntity(entity: Omit<Entity, 'id' | 'created' | 'updated' | 'logo'>, logoFile?: File): Promise<{ data: Entity | null; error: string | null }> {
    try {
      const formData = new FormData();
      formData.append('name', entity.name);
      formData.append('partner_id', entity.partner_id);
      
      if (entity.privacy_policy_url) {
        formData.append('privacy_policy_url', entity.privacy_policy_url);
      }
      if (entity.comment) {
        formData.append('comment', entity.comment);
      }
      
      // Handle logo: either file upload or URL
      if (logoFile) {
        formData.append('logo', logoFile);
        // Clear logo_url when file is uploaded
        formData.append('logo_url', '');
      } else if (entity.logo_url) {
        formData.append('logo_url', entity.logo_url);
      }
      
      const record = await pb.collection('entities').create(formData);
      
      const createdEntity: Entity = {
        id: record.id,
        name: record.name,
        logo: record.logo,
        logo_url: record.logo_url || (record.logo ? pb.files.getURL(record, record.logo) : undefined),
        privacy_policy_url: record.privacy_policy_url,
        comment: record.comment,
        partner_id: record.partner_id,
        created: record.created,
        updated: record.updated
      };
      
      return { data: createdEntity, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async updateEntity(id: string, updates: Partial<Omit<Entity, 'id' | 'created' | 'updated' | 'logo'>>, logoFile?: File): Promise<{ data: Entity | null; error: string | null }> {
    try {
      const formData = new FormData();
      
      if (updates.name !== undefined) {
        formData.append('name', updates.name);
      }
      if (updates.privacy_policy_url !== undefined) {
        formData.append('privacy_policy_url', updates.privacy_policy_url);
      }
      if (updates.comment !== undefined) {
        formData.append('comment', updates.comment);
      }
      
      // Handle logo: either file upload or URL
      if (logoFile) {
        formData.append('logo', logoFile);
        // Clear logo_url when file is uploaded
        formData.append('logo_url', '');
      } else if (updates.logo_url !== undefined) {
        formData.append('logo_url', updates.logo_url);
        // If URL is provided, clear the file field
        if (updates.logo_url) {
          formData.append('logo', '');
        }
      }
      
      const record = await pb.collection('entities').update(id, formData);
      
      const updatedEntity: Entity = {
        id: record.id,
        name: record.name,
        logo: record.logo,
        logo_url: record.logo_url || (record.logo ? pb.files.getURL(record, record.logo) : undefined),
        privacy_policy_url: record.privacy_policy_url,
        comment: record.comment,
        partner_id: record.partner_id,
        created: record.created,
        updated: record.updated
      };
      
      return { data: updatedEntity, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  static async deleteEntity(id: string): Promise<{ error: string | null }> {
    try {
      await pb.collection('entities').delete(id);
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }
}
